import React, { useEffect, useMemo, useState, useRef, useContext } from "react";
import { ChevronDown } from "lucide-react";
import { UserContext } from "../Common/UserContext";
import { Link } from "react-router-dom";
import ClientAppShell from "../components/ClientAppShell";
import ContentLoader from "../components/ContentLoader";
import { usePageWalkthrough } from "../hooks/usePageWalkthrough";
import { useAssessmentFormConfig, getSubCapabilityFormConfig } from "../hooks/useAssessmentFormConfig";
import {
  ratingToStateKey,
  stateKeyToRating,
  splitSectionDescription,
} from "../utils/assessmentFormConfig";
import Copilot from './components/assessmentCopilot';
import CapabilityAccessBanner from './components/CapabilityAccessBanner';
import { showAlert } from "../components/AppAlert/appAlert";
import {
  getAssessmentDetails,
  submitAssessment,
  getSubCapabilityAssessmentData,
  submitSMEState,
  fetchRequestStatus as fetchRequestStatusAPI,
  SubmitCapabilityRequest,
} from "../services/assessment";
import { getProjectState } from "../services/dashboard";
import ProgressBar from "./components/progress-bar";
import { AssessmentQuestionNav } from "../components/assessment/AssessmentQuestionNav";
import { AssessmentMaturityPicker } from "../components/assessment/AssessmentMaturityPicker";
import {
  AssessmentNavStatusIcon,
} from "../components/assessment/AssessmentNavStatusIcon";
import { getAssessmentUnitRatingStatus } from "../utils/assessmentNavUtils";
import { useAuth } from "../auth/AuthContext";
import {
  assessmentUnitKey,
  assessmentUnitTitle,
  findAssessmentRow,
  flattenAssessmentRows,
  isLeafSelectedCapability,
  isLeafUnit,
  lookupUnitState,
  sameAssessmentUnit,
} from "../utils/assessmentUnitUtils";
import "../assets/css/assessment.css";
import "../assets/css/progressbar.css";
import "../styles/assessment-page.css";
import { markClientNavPageViewed } from "../utils/clientNavReadState";
import { buildAxlPilotChatContext } from "../utils/buildAxlPilotChatContext";
import { buildReimagineAssessmentSnapshot } from "../utils/buildReimagineAssessmentSnapshot";
import {
  clearAxlPilotAssessmentSnapshot,
  setAxlPilotAssessmentSnapshot,
} from "../utils/axlPilotAssessmentCache";
import { getAskReimagineHealth } from "../services/askReimagineApi";

function Assessment() {
  const basename = process.env.PUBLIC_URL || "";
  const { user: authUser, isLoading: authLoading } = useAuth();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCapability, setExpandedCapability] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [questionIndex, setQuestionIndex] = useState(null);
  const [subCapabilityState, setSubCapabilityState] = useState({
    currentState: "",
    futureState: "",
    currentStatusDescription: "",
    futureStatusDescription: "",
    businessValue: "",
    businessValueImpact: "",
  });
  const [selectedSubCapability, setSelectedSubCapability] = useState({
    capabilityId: null,
    subCapabilityId: null,
    subCapability: "",
  });
  const [IsEnabled, setIsEnabled] = useState(false);
  const [isAllSubmitted, setIsAllSubmitted] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [subSelectedCard, setSubSelectedCard] = useState(null);
  const [subCapabilityStates, setSubCapabilityStates] = useState({});
  const [dirtySubCapabilities, setDirtySubCapabilities] = useState({});
  const [projectState, setProjectState] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [assessmentState, setAssessmentState] = useState(null);
  const dropdownRefs = useRef({});

  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);
  const activeTextAreaRef = useRef(null);
  const [isNextQues, setIsNextQues] = useState(1);
  const [showRequestButton, setShowRequestButton] = useState(false);
  const [requestStatus, setRequestStatus] = useState("Not Requested");
  const [isActive, setIsActive] = useState(false);
  const [rejectionComment, setRejectionComment] = useState(null);
  const { userId, projectId } = useContext(UserContext);

  const { formConfig } = useAssessmentFormConfig(data, selectedSubCapability);

  const { walkthrough } = usePageWalkthrough({
    userId,
    parentId: 3,
    targetMap: {
      '[data-nav="assessment"]': '[data-nav="assessment"]',
      '[data-element="subcapability-item"]:first': '[data-element="subcapability-item"]',
    },
  });

  const apiCallTracker = useRef({
    projectState: false,
    assessmentData: false,
    requestStatus: false,
  });

  const [progress, setProgress] = useState(0);
  const [assistantAvailable, setAssistantAvailable] = useState(true);

  const handleMenuSelection = (item) => {
    setSelectedItem(item);
    return item;
  };

  useEffect(() => {
    if (projectId) {
      fetchProjectState(projectId);
    }
  }, [projectId]);

  useEffect(() => {
    if (authLoading) return;
    if (!projectId && !localStorage.getItem("projectId")) {
      setLoading(false);
    }
  }, [authLoading, projectId, authUser]);

  useEffect(() => {
    document.body.classList.remove("modal-open");
    return () => {
      document.body.classList.remove("modal-open");
    };
  }, []);

  useEffect(() => {
    if (projectState !== null && projectId) {
      fetchAssessmentData(projectId);
    }
  }, [projectState]);

  useEffect(() => {
    if (projectId) {
      fetchRequestStatus();
    }
  }, [projectId]);

  useEffect(() => {
    if (userId && projectId) {
      markClientNavPageViewed("assessment", userId, projectId);
    }
  }, [userId, projectId]);

  useEffect(() => {
    if ("webkitSpeechRecognition" in window || "SpeechRecognition" in window) {
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = "en-US";

      recognitionRef.current.onresult = (event) => {
        const speechArray = Array.from(event.results);
        const finalTranscript = speechArray
          .map((result) => result[0].transcript)
          .join("");

        if (activeTextAreaRef.current === "current") {
          setSubCapabilityState((prev) => ({
            ...prev,
            currentStatusDescription:
              `${prev.currentStatusDescription} ${finalTranscript}`.trim(),
          }));
        } else if (activeTextAreaRef.current === "future") {
          setSubCapabilityState((prev) => ({
            ...prev,
            futureStatusDescription:
              `${prev.futureStatusDescription} ${finalTranscript}`.trim(),
          }));
        } else if (activeTextAreaRef.current === "businessValue") {
          setSubCapabilityState((prev) => ({
            ...prev,
            businessValue:
              `${prev.businessValue} ${finalTranscript}`.trim(),
          }));
        } else if (activeTextAreaRef.current === "businessValueImpact") {
          setSubCapabilityState((prev) => ({
            ...prev,
            businessValueImpact:
              `${prev.businessValueImpact} ${finalTranscript}`.trim(),
          }));
        }
      };

      recognitionRef.current.onerror = (event) => {
        console.error(`Speech recognition error: ${event.error}`);
        setIsListening(false);
      };
    } else {
      console.error("Speech-to-Text is not supported in this browser.");
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);

  const toggleSpeechRecognition = (textArea) => {
    if (!recognitionRef.current) {
      console.error("Speech Recognition is not initialized.");
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
      activeTextAreaRef.current = null;
    } else {
      recognitionRef.current.start();
      setIsListening(true);
      activeTextAreaRef.current = textArea;
    }
  };

  useEffect(() => {
    if (selectedSubCapability?.capabilityId != null) {
      const currentStateData =
        lookupUnitState(
          subCapabilityStates,
          selectedSubCapability.capabilityId,
          selectedSubCapability.subCapabilityId
        ) || {
          currentState: "",
          futureState: "",
          currentStatusDescription: "",
          futureStatusDescription: "",
          businessValue: "",
          businessValueImpact: "",
        };
      setSubCapabilityState(currentStateData);
    }
  }, [selectedSubCapability, subCapabilityStates]);

  const fetchProjectState = async (projectId) => {
    try {
      if (apiCallTracker.current.projectState) return;
      apiCallTracker.current.projectState = true;

      const payload = { userId, projectId: [parseInt(projectId, 10)] };
      const response = await getProjectState(payload);

      if (response.succeeded && response.data.length > 0) {
        const stateData = response.data.find(
          (item) => item.projectId === parseInt(projectId, 10)
        );
        if (stateData) {
          setProjectState(stateData.state);
        } else {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error("Failed to fetch project state:", error.message);
      setLoading(false);
    } finally {
      apiCallTracker.current.projectState = false;
    }
  };

  const fetchAssessmentData = async (projectId) => {
    if (apiCallTracker.current.assessmentData) return;
    apiCallTracker.current.assessmentData = true;

    try {
      const payload = { userId, projectId };

      const [assessmentData, capabilitiesData] = await Promise.all([
        getSubCapabilityAssessmentData(payload),
        getAssessmentDetails(payload),
      ]);

      setData(capabilitiesData);

      if (assessmentData?.data && capabilitiesData?.data?.selectedCapabilities) {
        const assessmentRows = flattenAssessmentRows(assessmentData.data);
        const newSubCapabilityStates = {};
        const newDirtyStates = {};
        let completedSubCapabilities = 0;
        let totalSubCapabilities = 0;
        let hasNewUnlockedCapabilities = false;

        capabilitiesData.data.selectedCapabilities.forEach((capability) => {
          totalSubCapabilities += capability.selectedSubCapabilities.length;
        });

        capabilitiesData.data.selectedCapabilities.forEach((capability) => {
          capability.selectedSubCapabilities.forEach((subCapability) => {
            const unitKey = assessmentUnitKey(
              capability.capabilityId,
              subCapability.subCapabilityId
            );
            const matchedSubCapability = findAssessmentRow(
              assessmentRows,
              capability.capabilityId,
              subCapability.subCapabilityId
            );

            const subFormConfig = getSubCapabilityFormConfig(subCapability);

            const subCapabilityStateData = matchedSubCapability
              ? {
                  currentState: ratingToStateKey(
                    matchedSubCapability.currentStatusRating,
                    subFormConfig
                  ),
                  futureState: ratingToStateKey(
                    matchedSubCapability.futureStatusRating,
                    subFormConfig
                  ),
                  currentStatusDescription:
                    matchedSubCapability.currentStatusDescription,
                  futureStatusDescription:
                    matchedSubCapability.futureStatusDescription,
                  businessValue: matchedSubCapability.businessValue || "",
                  businessValueImpact: matchedSubCapability.businessValueImpact || "",
                  isNewLocked: subCapability.isNewLocked,
                  isActive: subCapability.isActive,
                  isCompleted: subCapability.state === "4",
                }
              : {
                  currentState: "",
                  futureState: "",
                  currentStatusDescription: "",
                  futureStatusDescription: "",
                  businessValue: "",
                  businessValueImpact: "",
                  isNewLocked: subCapability.isNewLocked,
                  isActive: subCapability.isActive,
                  isCompleted: false,
                };

            newSubCapabilityStates[unitKey] = subCapabilityStateData;
            newDirtyStates[unitKey] = false;

            if (!subCapabilityStateData.isNewLocked) {
              hasNewUnlockedCapabilities = true;
            }

            if (
              selectedSubCapability &&
              sameAssessmentUnit(
                capability.capabilityId,
                subCapability.subCapabilityId,
                selectedSubCapability.capabilityId,
                selectedSubCapability.subCapabilityId
              )
            ) {
              setSubCapabilityState(subCapabilityStateData);
            }

            if (matchedSubCapability) {
              completedSubCapabilities++;
            }
          });
        });

        const progressPercentage =
          totalSubCapabilities > 0
            ? Math.round(
                (completedSubCapabilities / totalSubCapabilities) * 100
              )
            : 0;
        setProgress(progressPercentage);

        setSubCapabilityStates(newSubCapabilityStates);
        setDirtySubCapabilities(newDirtyStates);
        
        const allSubmitted = checkAllSubmitted(
          assessmentData,
          capabilitiesData
        );

        capabilitiesData.data.selectedCapabilities.forEach((capability) => {
          capability.selectedSubCapabilities.forEach((subCapability) => {
            setAssessmentState(subCapability.state);
            if (subCapability.state === "3" && allSubmitted) {
              setIsEnabled(true);
            } else if (subCapability.state === "4" && allSubmitted) {
              setIsAllSubmitted(allSubmitted);
            }
          });
        });

        setShowRequestButton(!hasNewUnlockedCapabilities);

      }
    } catch (error) {
      console.error("Error fetching assessment data:", error.message);
    } finally {
      apiCallTracker.current.assessmentData = false;
      setLoading(false);
    }
  };

  const findSubCapabilityContext = (capabilityId, subCapabilityId) => {
    const capabilities = data?.data?.selectedCapabilities ?? [];
    for (const capability of capabilities) {
      const subCapability = capability.selectedSubCapabilities?.find((sub) =>
        sameAssessmentUnit(
          capability.capabilityId,
          sub.subCapabilityId,
          capabilityId,
          subCapabilityId
        )
      );
      if (subCapability) {
        return { capabilityId: capability.capabilityId, subCapability };
      }
    }
    return { capabilityId: selectedSubCapability?.capabilityId ?? null, subCapability: null };
  };

  const getModifiedSubCapabilities = () => {
    const capabilities = data?.data?.selectedCapabilities ?? [];
    const modified = [];

    capabilities.forEach((capability) => {
      (capability.selectedSubCapabilities || []).forEach((subCapability) => {
        const unitKey = assessmentUnitKey(
          capability.capabilityId,
          subCapability.subCapabilityId
        );
        if (!dirtySubCapabilities[unitKey]) return;

        const isCurrent = sameAssessmentUnit(
          capability.capabilityId,
          subCapability.subCapabilityId,
          selectedSubCapability?.capabilityId,
          selectedSubCapability?.subCapabilityId
        );
        const { subCapability: unit } = findSubCapabilityContext(
          capability.capabilityId,
          subCapability.subCapabilityId
        );
        const subCapabilityData = isCurrent
          ? subCapabilityState
          : subCapabilityStates[unitKey] || {
              currentState: "",
              futureState: "",
              currentStatusDescription: "",
              futureStatusDescription: "",
              businessValue: "",
              businessValueImpact: "",
            };

        const itemFormConfig =
          isCurrent || !unit ? formConfig : getSubCapabilityFormConfig(unit);

        modified.push({
          capabilityId: capability.capabilityId,
          subCapabilityId: isLeafUnit(subCapability.subCapabilityId)
            ? null
            : Number(subCapability.subCapabilityId),
          currentStatusDescription: subCapabilityData.currentStatusDescription,
          futureStatusDescription: subCapabilityData.futureStatusDescription,
          businessValue: subCapabilityData.businessValue,
          businessValueImpact: subCapabilityData.businessValueImpact,
          currentStatusRating: stateKeyToRating(
            subCapabilityData.currentState,
            itemFormConfig
          ),
          futureStatusRating: stateKeyToRating(
            subCapabilityData.futureState,
            itemFormConfig
          ),
        });
      });
    });

    return modified;
  };

  const handleBackQuestion = async () => {
    if (questionIndex > 0) {
      const capabilities = data.data.selectedCapabilities;

      for (let i = capabilities.length - 1; i >= 0; i--) {
        const capability = capabilities[i];

        for (
          let j = capability.selectedSubCapabilities.length - 1;
          j >= 0;
          j--
        ) {
          const subCapability = capability.selectedSubCapabilities[j];

          if (
            sameAssessmentUnit(
              capability.capabilityId,
              subCapability.subCapabilityId,
              selectedSubCapability.capabilityId,
              selectedSubCapability.subCapabilityId
            )
          ) {
            if (j - 1 >= 0) {
              const modifiedSubCapabilities = getModifiedSubCapabilities();

              if (modifiedSubCapabilities.length > 0) {
                if (!validateModifiedSubCapabilities(modifiedSubCapabilities)) {
                  return;
                }

                if (
                  subCapabilityState.currentState &&
                  subCapabilityState.currentStatusDescription.trim() &&
                  subCapabilityState.futureState &&
                  subCapabilityState.futureStatusDescription.trim()
                ) {
                  setIsNextQues(0);
                  await handleSubmit();
                }
              }

              const prevSubCapability =
                capability.selectedSubCapabilities[j - 1];
              setSelectedSubCapability({
                capabilityId: capability.capabilityId,
                subCapabilityId: prevSubCapability.subCapabilityId,
                subCapability: prevSubCapability.subCapability,
              });

              setSubSelectedCard(
                assessmentUnitKey(
                  capability.capabilityId,
                  prevSubCapability.subCapabilityId
                )
              );
              setSelectedCard(capability.capabilityId);
              setExpandedCapability(capability.capabilityId);
              setQuestionIndex((prevIndex) => prevIndex - 1);
              await fetchAssessmentData(projectId);
              return;
            }

            if (i - 1 >= 0) {
              const modifiedSubCapabilities = getModifiedSubCapabilities();

              if (modifiedSubCapabilities.length > 0) {
                if (!validateModifiedSubCapabilities(modifiedSubCapabilities)) {
                  return;
                }

                if (
                  subCapabilityState.currentState &&
                  subCapabilityState.currentStatusDescription.trim() &&
                  subCapabilityState.futureState &&
                  subCapabilityState.futureStatusDescription.trim()
                ) {
                  setIsNextQues(0);
                  await handleSubmit();
                }
              }

              const prevCapability = capabilities[i - 1];
              const lastSubCapability =
                prevCapability.selectedSubCapabilities[
                  prevCapability.selectedSubCapabilities.length - 1
                ];
              setSelectedSubCapability({
                capabilityId: prevCapability.capabilityId,
                subCapabilityId: lastSubCapability.subCapabilityId,
                subCapability: lastSubCapability.subCapability,
              });
              setSubSelectedCard(
                assessmentUnitKey(
                  prevCapability.capabilityId,
                  lastSubCapability.subCapabilityId
                )
              );
              setSelectedCard(prevCapability.capabilityId);
              setExpandedCapability(prevCapability.capabilityId);
              await fetchAssessmentData(projectId);
              return;
            }
          }
        }
      }
    } else {
      showAlert("You're already on the first question.", "info");
    }
  };

  const handleNextQuestion = async () => {
    const nextSubCapability = findNextSubCapability();

    if (nextSubCapability) {
      if (isNextQues === 1) {
        setQuestionIndex((prevIndex) => prevIndex + 1);
        setSelectedSubCapability(nextSubCapability);
        setSubSelectedCard(
          assessmentUnitKey(
            nextSubCapability.capabilityId,
            nextSubCapability.subCapabilityId
          )
        );
        setSelectedCard(nextSubCapability.capabilityId);
        setExpandedCapability(nextSubCapability.capabilityId);
        await fetchAssessmentData(projectId);
      }
    } else {
      if (assessmentState === "3") {
        await fetchAssessmentData(projectId);
      } else {
        showAlert(
          "You've completed all the assessments — there are no more questions.",
          "success"
        );
        await fetchAssessmentData(projectId);
      }
    }
  };

  const findNextSubCapability = () => {
    if (!data || !selectedSubCapability) return null;

    const capabilities = data.data.selectedCapabilities;

    for (let i = 0; i < capabilities.length; i++) {
      const capability = capabilities[i];

      for (let j = 0; j < capability.selectedSubCapabilities.length; j++) {
        const subCapability = capability.selectedSubCapabilities[j];

        // Find the next sub-capability after the current one
        if (
          sameAssessmentUnit(
            capability.capabilityId,
            subCapability.subCapabilityId,
            selectedSubCapability.capabilityId,
            selectedSubCapability.subCapabilityId
          )
        ) {
          // Check if there is another sub-capability in the same capability
          if (j + 1 < capability.selectedSubCapabilities.length) {
            setIsNextQues(1);
            return {
              capabilityId: capability.capabilityId,
              subCapabilityId:
                capability.selectedSubCapabilities[j + 1].subCapabilityId,
              subCapability:
                capability.selectedSubCapabilities[j + 1].subCapability,
            };
          }

          // If no more sub-capabilities, jump to the next capability
          if (i + 1 < capabilities.length) {
            const nextCapability = capabilities[i + 1];
            setIsNextQues(1);
            return {
              capabilityId: nextCapability.capabilityId,
              subCapabilityId:
                nextCapability.selectedSubCapabilities[0].subCapabilityId,
              subCapability:
                nextCapability.selectedSubCapabilities[0].subCapability,
            };
          }
        }
      }
    }

    return null;
  };

  const getSubCapabilitiesCount = () => {
    if (!data || !data.data || !data.data.selectedCapabilities) return 0;

    return data.data.selectedCapabilities.reduce(
      (total, capability) => total + capability.selectedSubCapabilities.length,
      0
    );
  };

  const getQuestionIndexFromSelection = () => {
    if (!data || !data.data || !data.data.selectedCapabilities) return 0;

    let index = 0;
    for (let i = 0; i < data.data.selectedCapabilities.length; i++) {
      const capability = data.data.selectedCapabilities[i];
      for (let j = 0; j < capability.selectedSubCapabilities.length; j++) {
        const subCapability = capability.selectedSubCapabilities[j];

        if (
          sameAssessmentUnit(
            capability.capabilityId,
            subCapability.subCapabilityId,
            selectedSubCapability.capabilityId,
            selectedSubCapability.subCapabilityId
          )
        ) {
          return index;
        }
        index++;
      }
    }
    return 0;
  };

  useEffect(() => {
    if (selectedSubCapability) {
      const newIndex = getQuestionIndexFromSelection();
      setQuestionIndex(newIndex);
    }
  }, [selectedSubCapability]);

  useEffect(() => {
    if (selectedCard) {
      const capabilityElement = document.getElementById(
        `capability-${selectedCard}`
      );
      capabilityElement?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }

    if (subSelectedCard) {
      const subCapabilityElement = document.getElementById(
        `sub-capability-${subSelectedCard}`
      );
      subCapabilityElement?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [selectedCard, subSelectedCard]);

  const toggleDropdown = (capabilityId) => {
    const modifiedSubCapabilities = getModifiedSubCapabilities();

    if (modifiedSubCapabilities.length > 0) {
      if (!validateModifiedSubCapabilities(modifiedSubCapabilities)) {
        return;
      }

      if (
        subCapabilityState.currentState &&
        subCapabilityState.currentStatusDescription.trim() &&
        subCapabilityState.futureState &&
        subCapabilityState.futureStatusDescription.trim()
      ) {
        handleSubmit();
      }
    }

    setExpandedCapability((prev) =>
      prev === capabilityId ? null : capabilityId
    );
    setSelectedCard(capabilityId);
  };

  const handleSubmitClick = (
    capabilityId,
    subCapabilityId,
    subCapabilityName
  ) => {
    const modifiedSubCapabilities = getModifiedSubCapabilities();

    if (modifiedSubCapabilities.length > 0) {
      if (!validateModifiedSubCapabilities(modifiedSubCapabilities)) {
        return;
      }

      if (
        subCapabilityState.currentState &&
        subCapabilityState.currentStatusDescription.trim() &&
        subCapabilityState.futureState &&
        subCapabilityState.futureStatusDescription.trim()
      ) {
        handleSubmit();
      }
    }

    setSelectedSubCapability({
      capabilityId: capabilityId,
      subCapabilityId: subCapabilityId,
      subCapability: subCapabilityName,
    });

    setSubSelectedCard(assessmentUnitKey(capabilityId, subCapabilityId));
    setSelectedCard(capabilityId);
    setExpandedCapability(capabilityId);
    setShowModal(true);
  };

  const handleImpactChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "");
    if (digits === "") {
      handleInputChange("businessValueImpact", "");
      return;
    }
    if (/^\d{1,3}$/.test(digits) && Number(digits) <= 100) {
      handleInputChange("businessValueImpact", digits);
    }
  };

  const handleInputChange = (field, value) => {
    const unitKey = assessmentUnitKey(
      selectedSubCapability.capabilityId,
      selectedSubCapability.subCapabilityId
    );

    setSubCapabilityState((prevState) => ({
      ...prevState,
      [field]: value,
    }));

    setDirtySubCapabilities((prevState) => ({
      ...prevState,
      [unitKey]: true,
    }));
  };

  const checkAllSubmitted = (assessmentData, capabilitiesData) => {
    return capabilitiesData.data.selectedCapabilities.every((cap) =>
      cap.selectedSubCapabilities.every((subCap) => {
        const assessment = findAssessmentRow(
          flattenAssessmentRows(assessmentData.data),
          cap.capabilityId,
          subCap.subCapabilityId
        );
        return assessment;
      })
    );
  };

  const validateModifiedSubCapabilities = (modifiedSubCapabilities) => {
    const incompleteSubCapabilities = modifiedSubCapabilities.filter(
      (subCap) => {
        return (
          !subCap.currentStatusDescription.trim() ||
          !subCap.futureStatusDescription.trim() ||
          !subCap.currentStatusRating ||
          !subCap.futureStatusRating
        );
      }
    );

    if (incompleteSubCapabilities.length > 0) {
      showAlert("Please complete the assessment before moving forward.", "warning");
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    const modifiedSubCapabilities = getModifiedSubCapabilities();

    if (modifiedSubCapabilities.length > 0) {
      if (!validateModifiedSubCapabilities(modifiedSubCapabilities)) {
        return;
      }

      const payload = {
        userId: userId,
        projectId: projectId,
        subCapabilitiesAssessments: modifiedSubCapabilities,
      };

      try {
        const response = await submitAssessment(payload);
        if (response) {
          setDirtySubCapabilities((prevState) => {
            const newState = { ...prevState };
            modifiedSubCapabilities.forEach((subCap) => {
              newState[assessmentUnitKey(subCap.capabilityId, subCap.subCapabilityId)] = false;
            });
            return newState;
          });

          if (isNextQues === 1) {
            await handleNextQuestion();
          }
        }
      } catch (error) {
        console.error("Error submitting data:", error);
        showAlert("We couldn't save your data. Please try again.", "error");
      }
    } else {
      setIsNextQues(1);
      await handleNextQuestion();
    }
  };

  const handleFinalSubmit = async () => {
    const payload = {
      userId: userId,
      projectId: projectId,
      state: 0,
    };

    try {
      const response = await submitSMEState(payload);
      if (response) {
        setIsEnabled(false);
        showAlert("Your assessment was submitted successfully.", "success");

        // Return to closed list state — reopen capability → sub-capability to review
        setShowModal(false);
        setExpandedCapability(null);
        setSelectedCard(null);
        setSubSelectedCard(null);
        setSelectedSubCapability({
          capabilityId: null,
          subCapabilityId: null,
          subCapability: "",
        });

        await fetchAssessmentData(projectId);
      }
    } catch (error) {
      console.error("Error submitting final state:", error.message);
    }
  };

  const fetchRequestStatus = async () => {
    try {
      if (apiCallTracker.current.requestStatus) return;
      apiCallTracker.current.requestStatus = true;

      const payload = { userId, projectId };
      const data = await fetchRequestStatusAPI(payload);

      if (data && data.data) {
        setRequestStatus(data.data.status || "Not Requested");
        setIsActive(data.data.isActive || false);

        if (data.data.status === "Rejected") {
          setRejectionComment(data.data.comments || "No comments provided.");
        } else {
          setRejectionComment(null);
        }
      } else {
        setRequestStatus("Not Requested");
        setRejectionComment(null);
      }
    } catch (error) {
      console.error("Error fetching request status:", error);
      setRequestStatus("Not Requested");
      setRejectionComment(null);
    } finally {
      apiCallTracker.current.requestStatus = false;
    }
  };

  const handleRequest = async () => {
    // Only block when a request is already in flight or capabilities are
    // currently unlocked. Every other status (Not Requested, Rejected,
    // Approved-but-inactive, Processed, etc.) is allowed to (re)request.
    if (requestStatus === "Pending" || (requestStatus === "Approved" && isActive)) {
      return;
    }

    if (!userId || !projectId) {
      showAlert("We couldn't identify your project. Please reopen it and try again.", "error");
      return;
    }

    try {
      const payload = { userId, projectId, status: "Pending" };
      const response = await SubmitCapabilityRequest(payload);

      if (response && response.data) {
        setRequestStatus("Pending");
        showAlert("Your request to add capabilities has been sent to your advisor.", "success");
      } else {
        showAlert("We couldn't submit your request. Please try again.", "error");
      }
    } catch (error) {
      console.error("Error submitting capability request:", error);
      showAlert("We couldn't submit your request. Please try again.", "error");
    }
  };

  const resolveUnitFormState = (capabilityId, subCapabilityId) => {
    const unitKey = assessmentUnitKey(capabilityId, subCapabilityId);
    if (
      sameAssessmentUnit(
        capabilityId,
        subCapabilityId,
        selectedSubCapability?.capabilityId,
        selectedSubCapability?.subCapabilityId
      )
    ) {
      return subCapabilityState;
    }
    return subCapabilityStates[unitKey];
  };

  const getNavStatus = (capabilityId, subCapabilityId) => {
    const state = resolveUnitFormState(capabilityId, subCapabilityId);
    if (state?.isCompleted) return "complete";
    return getAssessmentUnitRatingStatus(state);
  };

  useEffect(() => {
    const caps = data?.data?.selectedCapabilities;
    if (!caps?.length || expandedCapability != null) return;
    const firstWithSubs = caps.find((cap) => !isLeafSelectedCapability(cap));
    if (firstWithSubs) {
      setExpandedCapability(firstWithSubs.capabilityId);
    }
  }, [data, expandedCapability]);

  const countRatedUnits = () => {
    if (!data?.data?.selectedCapabilities) return 0;
    let count = 0;
    data.data.selectedCapabilities.forEach((capability) => {
      capability.selectedSubCapabilities.forEach((sub) => {
        if (getNavStatus(capability.capabilityId, sub.subCapabilityId) === "complete") {
          count += 1;
        }
      });
    });
    return count;
  };

  const totalAssessmentUnits = getSubCapabilitiesCount();
  const ratedUnitsCount = countRatedUnits();

  const selectedCapabilityRecord = data?.data?.selectedCapabilities?.find(
    (cap) => cap.capabilityId === selectedSubCapability?.capabilityId
  );
  const isSelectedLeafUnit =
    selectedCapabilityRecord &&
    isLeafSelectedCapability(selectedCapabilityRecord);

  useEffect(() => {
    if (!userId || !projectId || !data) {
      setAxlPilotAssessmentSnapshot(null);
      return;
    }

    const snapshot = buildReimagineAssessmentSnapshot({
      userId,
      projectId,
      authUser,
      capabilitiesPayload: data,
    });
    setAxlPilotAssessmentSnapshot(snapshot);

    return () => {
      clearAxlPilotAssessmentSnapshot();
    };
  }, [data, userId, projectId, authUser]);

  useEffect(() => {
    if (!userId || !projectId || loading) {
      return;
    }

    let cancelled = false;
    getAskReimagineHealth()
      .then((health) => {
        if (cancelled) return;
        setAssistantAvailable(health?.status?.toLowerCase() === "ok");
      })
      .catch(() => {
        if (!cancelled) {
          setAssistantAvailable(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [userId, projectId, loading]);

  const axlPilotChatContext = useMemo(() => {
    if (!showModal || selectedSubCapability?.capabilityId == null) {
      return null;
    }
    return buildAxlPilotChatContext(
      selectedSubCapability.capabilityId,
      selectedSubCapability.subCapabilityId
    );
  }, [
    showModal,
    selectedSubCapability?.capabilityId,
    selectedSubCapability?.subCapabilityId,
    data,
  ]);

  return (
     <>
      {walkthrough}
      <ClientAppShell
        title="Assessment"
        dataNav="assessment"
        selectedItem={selectedItem}
        onMenuSelect={handleMenuSelection}
        containerClassName="assessment-page"
      >
        <div className="asses-page-shell asses-frame-parent1">
              <header className="asses-page-header">
                <div>
                  <p className="asses-page-step">Step 2 of 4</p>
                  <h2 className="asses-page-title">
                    Rate where you are and where you want to be
                  </h2>
                  <p className="asses-page-lead">
                    For each item, pick a current and target maturity level and explain
                    why.
                  </p>
                </div>
              </header>

              {IsEnabled ? (
                <div className="asses-save-progress-actions">
                  <button
                    type="button"
                    className="btn btn-dashboard-primary asses-save-progress-button"
                    onClick={handleFinalSubmit}
                  >
                    Submit
                  </button>
                </div>
              ) : null}

              <div className="asses-frame-parent21">
                <div className="asses-frame-parent22">
                  {requestStatus === "Approved" ? (
                    isActive ? (
                      <CapabilityAccessBanner
                        variant="unlocked"
                        ctaTo="/capabilities"
                        ctaLabel="Select capabilities"
                      />
                    ) : (
                      <CapabilityAccessBanner
                        variant="locked"
                        onRequest={handleRequest}
                      />
                    )
                  ) : requestStatus === "Rejected" ? (
                    <CapabilityAccessBanner
                      variant="rejected"
                      onRequest={handleRequest}
                      rejectionComment={rejectionComment}
                    />
                  ) : (
                    showRequestButton && (
                      <CapabilityAccessBanner
                        variant={requestStatus === "Pending" ? "pending" : "locked"}
                        onRequest={handleRequest}
                      />
                    )
                  )}
                </div>
              </div>

              {loading ? (
                <div className="asses-workspace asses-workspace--loading">
                  <ContentLoader page label="Loading assessment…" />
                </div>
              ) : !data || !data.data.selectedCapabilities.length ? (
                <div className="asses-workspace asses-workspace--empty">
                  <div className="asses-empty-state" role="status">
                    <h3 className="asses-empty-state__title">
                      Select capabilities to start your assessment
                    </h3>
                    <p className="asses-empty-state__lead">
                      Choose the areas you want to rate on the capabilities step. You can
                      return here once at least one capability is selected.
                    </p>
                    <Link
                      to="/capabilities"
                      className="btn btn-dashboard-primary asses-empty-state__cta"
                    >
                      Go to capabilities
                    </Link>
                  </div>
                </div>
              ) : (
              <div
                className="asses-workspace asses-frame-parent4"
                data-element="subcapability-item"
              >
                <nav className="asses-nav-tree asses-strategic-management-wrapper" aria-label="Assessment areas">
                  {totalAssessmentUnits > 0 ? (
                    <div className="asses-nav-sidebar-progress">
                      <div className="asses-nav-sidebar-progress__row">
                        <span className="asses-nav-sidebar-progress__title">Progress</span>
                        <span className="asses-nav-sidebar-progress__meta">
                          <strong>{ratedUnitsCount}</strong> of {totalAssessmentUnits} rated
                        </span>
                      </div>
                      <ProgressBar
                        progress={
                          totalAssessmentUnits > 0
                            ? Math.round((ratedUnitsCount / totalAssessmentUnits) * 100)
                            : progress
                        }
                      />
                    </div>
                  ) : null}

                  <div className="asses-nav-cap-list">
                    {data.data.selectedCapabilities.map((capability) => {
                      const leaf = isLeafSelectedCapability(capability);
                      const firstUnit = capability.selectedSubCapabilities?.[0];
                      const selectedUnitKey = assessmentUnitKey(
                        selectedSubCapability?.capabilityId,
                        selectedSubCapability?.subCapabilityId
                      );
                      const groupTotal = capability.selectedSubCapabilities.length;
                      const groupRated = capability.selectedSubCapabilities.filter(
                        (sub) =>
                          getNavStatus(
                            capability.capabilityId,
                            sub.subCapabilityId
                          ) === "complete"
                      ).length;
                      const isExpanded = expandedCapability === capability.capabilityId;

                      if (leaf && firstUnit) {
                        const unitKey = assessmentUnitKey(
                          capability.capabilityId,
                          firstUnit.subCapabilityId
                        );
                        const isItemActive = selectedUnitKey === unitKey;
                        const status = getNavStatus(
                          capability.capabilityId,
                          firstUnit.subCapabilityId
                        );

                        return (
                          <div className="asses-nav-block asses-nav-block--leaf" key={capability.capabilityId}>
                            <button
                              type="button"
                              id={`capability-${capability.capabilityId}`}
                              className={`asses-nav-cap-row asses-nav-cap-row--leaf${
                                isItemActive ? " asses-nav-cap-row--selected" : ""
                              }`}
                              onClick={() =>
                                handleSubmitClick(
                                  capability.capabilityId,
                                  firstUnit.subCapabilityId,
                                  firstUnit.subCapability || capability.capability
                                )
                              }
                            >
                              <AssessmentNavStatusIcon status={status} />
                              <span className="asses-nav-cap-row__label">
                                {capability.capability}
                              </span>
                            </button>
                          </div>
                        );
                      }

                      return (
                        <div
                          className={`asses-nav-block asses-nav-block--group${
                            isExpanded ? " asses-nav-block--expanded" : ""
                          }`}
                          key={capability.capabilityId}
                        >
                          <button
                            type="button"
                            id={`capability-${capability.capabilityId}`}
                            className="asses-nav-cap-row asses-nav-cap-row--parent"
                            onClick={() => toggleDropdown(capability.capabilityId)}
                            aria-expanded={isExpanded}
                          >
                            <ChevronDown
                              className={`asses-nav-cap-row__chevron${
                                isExpanded ? " asses-nav-cap-row__chevron--open" : ""
                              }`}
                              size={14}
                              strokeWidth={2}
                              aria-hidden
                            />
                            <span className="asses-nav-cap-row__label">
                              {capability.capability}
                            </span>
                            <span className="asses-nav-cap-row__meta">
                              {groupRated} of {groupTotal} rated
                            </span>
                          </button>

                          {isExpanded ? (
                            <div
                              ref={(el) =>
                                (dropdownRefs.current[capability.capabilityId] = el)
                              }
                              className="asses-nav-sub-panel asses-sub-capability-dropdown"
                            >
                              {capability.selectedSubCapabilities.map((subCapability) => {
                                const unitKey = assessmentUnitKey(
                                  capability.capabilityId,
                                  subCapability.subCapabilityId
                                );
                                const isItemActive = selectedUnitKey === unitKey;
                                const status = getNavStatus(
                                  capability.capabilityId,
                                  subCapability.subCapabilityId
                                );

                                return (
                                  <button
                                    type="button"
                                    key={unitKey}
                                    id={`sub-capability-${unitKey}`}
                                    className={`asses-nav-sub-item${
                                      isItemActive ? " asses-nav-sub-item--selected" : ""
                                    }`}
                                    onClick={() =>
                                      handleSubmitClick(
                                        capability.capabilityId,
                                        subCapability.subCapabilityId,
                                        subCapability.subCapability
                                      )
                                    }
                                  >
                                    <span className="asses-nav-sub-item__icon-wrap">
                                      <AssessmentNavStatusIcon status={status} />
                                    </span>
                                    <span className="asses-nav-sub-item__label">
                                      {subCapability.subCapability}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </nav>
                {showModal && (
                  <div className="asses-form-panel asses-frame-wrapper12">
                    <div className="asses-form-panel__header">
                      {isSelectedLeafUnit ? (
                        <span className="asses-form-panel__tag">Single capability</span>
                      ) : null}
                      <h2 className="asses-form-panel__title">
                        {assessmentUnitTitle(
                          selectedSubCapability?.capabilityId,
                          selectedSubCapability?.subCapabilityId,
                          selectedSubCapability?.subCapability
                        )}
                      </h2>
                    </div>
                    <div
                      className="asses-form-panel__divider"
                      role="presentation"
                      aria-hidden="true"
                    />
                    <div className="asses-sub-capability-dataai-vision-parent">
                      <div className="asses-vector-parent">
                        <img
                          className="asses-vector-icon"
                          alt=""
                          src={`${basename}/images/Vector 2.svg`}
                        />
                        <div className="asses-states-layout">
                          <div className="asses-states-body">
                            <div className="asses-state-column asses-frame-parent11 asses-state-column--current">
                            <div className="asses-current-state-parent">
                              <div className="asses-strategic-management required">
                                {formConfig.currentSection.title}
                              </div>
                              <div className="asses-state-prompt-scroll">
                                <ul className="asses-state-prompt-list asses-describe-the-current">
                                  {splitSectionDescription(
                                    formConfig.currentSection.description
                                  ).map((line, index) => (
                                    <li key={`current-prompt-${index}`}>{line}</li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                            <AssessmentMaturityPicker
                              name={formConfig.currentSection.title}
                              value={subCapabilityState.currentState}
                              disabled={subCapabilityState.isCompleted}
                              formConfig={formConfig}
                              onChange={(state) =>
                                handleInputChange("currentState", state)
                              }
                            />

                            <div className="asses-frame-wrapper13 asses-state-comment-wrap">
                              <label
                                className="asses-comment-label required"
                                htmlFor="asses-current-state-comment"
                              >
                                Why this level?
                              </label>
                              <div className="asses-textarea-with-mic">
                                <textarea
                                  id="asses-current-state-comment"
                                  className="asses-custom-textarea asses-state-comment"
                                  placeholder={formConfig.currentSection.commentPlaceholder}
                                  style={{ whiteSpace: "pre-wrap" }}
                                  value={
                                    subCapabilityState.currentStatusDescription
                                  }
                                  onChange={(e) =>
                                    handleInputChange(
                                      "currentStatusDescription",
                                      e.target.value
                                    )
                                  }
                                  disabled={subCapabilityState.isCompleted}
                                />
                                <img
                                  src={`${basename}/images/mic.svg`}
                                  alt="Mic Icon"
                                  onClick={() => {
                                    if (!subCapabilityState.isCompleted) {
                                      toggleSpeechRecognition("current");
                                    }
                                  }}
                                  className={`mic-icon ${
                                    subCapabilityState.isCompleted
                                      ? "disabled"
                                      : ""
                                  }`}
                                  style={{
                                    filter:
                                      isListening &&
                                      activeTextAreaRef.current === "current"
                                        ? "grayscale(1)"
                                        : "grayscale(0)",
                                  }}
                                />
                              </div>
                              {formConfig.currentSection.commentExample ? (
                                <p className="asses-comment-example">
                                  {formConfig.currentSection.commentExample}
                                </p>
                              ) : null}
                            </div>
                          </div>
                          <div className="asses-state-divider" aria-hidden="true" />
                          <div className="asses-state-column asses-frame-parent11 asses-state-column--future">
                            <div className="asses-current-state-parent">
                              <div className="asses-strategic-management required">
                                {formConfig.futureSection.title}
                              </div>
                              <div className="asses-state-prompt-scroll">
                                <ul className="asses-state-prompt-list asses-describe-the-current">
                                  {splitSectionDescription(
                                    formConfig.futureSection.description
                                  ).map((line, index) => (
                                    <li key={`future-prompt-${index}`}>{line}</li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                            <AssessmentMaturityPicker
                              name={formConfig.futureSection.title}
                              value={subCapabilityState.futureState}
                              disabled={subCapabilityState.isCompleted}
                              formConfig={formConfig}
                              onChange={(state) =>
                                handleInputChange("futureState", state)
                              }
                            />
                            <div className="asses-frame-wrapper13 asses-state-comment-wrap">
                              <label
                                className="asses-comment-label required"
                                htmlFor="asses-future-state-comment"
                              >
                                Why this level?
                              </label>
                              <div className="asses-textarea-with-mic">
                                <textarea
                                  id="asses-future-state-comment"
                                  className="asses-custom-textarea asses-state-comment"
                                  placeholder={formConfig.futureSection.commentPlaceholder}
                                  style={{ whiteSpace: "pre-wrap" }}
                                  value={
                                    subCapabilityState.futureStatusDescription
                                  }
                                  onChange={(e) =>
                                    handleInputChange(
                                      "futureStatusDescription",
                                      e.target.value
                                    )
                                  }
                                  disabled={subCapabilityState.isCompleted}
                                />
                                <img
                                  src={`${basename}/images/mic.svg`}
                                  alt="Mic Icon"
                                  onClick={() => {
                                    if (!subCapabilityState.isCompleted) {
                                      toggleSpeechRecognition("future");
                                    }
                                  }}
                                  className={`mic-icon ${
                                    subCapabilityState.isCompleted
                                      ? "disabled"
                                      : ""
                                  }`}
                                  style={{
                                    filter:
                                      isListening &&
                                      activeTextAreaRef.current === "future"
                                        ? "grayscale(1)"
                                        : "grayscale(0)",
                                  }}
                                />
                              </div>
                              {formConfig.futureSection.commentExample ? (
                                <p className="asses-comment-example">
                                  {formConfig.futureSection.commentExample}
                                </p>
                              ) : null}
                            </div>
                          </div>
                          </div>
                          <div className="asses-bc">
                            <p className="asses-bc__title">
                              Business case · optional
                            </p>
                            <div className="asses-bc__row">
                              <div className="asses-bc__field asses-bc__field--value">
                                <label
                                  className="asses-bc__label"
                                  htmlFor="asses-business-value"
                                >
                                  {formConfig.businessValueTitle}
                                </label>
                                <input
                                  id="asses-business-value"
                                  type="text"
                                  className="asses-bc__value-input"
                                  placeholder={formConfig.businessValuePlaceholder}
                                  value={subCapabilityState.businessValue}
                                  onChange={(e) =>
                                    handleInputChange("businessValue", e.target.value)
                                  }
                                  disabled={subCapabilityState.isCompleted}
                                />
                              </div>
                              <div className="asses-bc__field asses-bc__field--impact">
                                <label
                                  className="asses-bc__label"
                                  htmlFor="asses-business-impact"
                                >
                                  {formConfig.businessImpactTitle}
                                </label>
                                <div className="asses-bc__impact-shell">
                                  <input
                                    id="asses-business-impact"
                                    type="text"
                                    inputMode="numeric"
                                    className="asses-bc__impact-input"
                                    placeholder={formConfig.businessImpactPlaceholder}
                                    value={subCapabilityState.businessValueImpact}
                                    onChange={handleImpactChange}
                                    maxLength={3}
                                    disabled={subCapabilityState.isCompleted}
                                  />
                                  <span className="asses-bc__impact-suffix" aria-hidden="true">
                                    %
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <AssessmentQuestionNav
                        questionIndex={questionIndex}
                        totalQuestions={getSubCapabilitiesCount()}
                        onPrevious={handleBackQuestion}
                        onNext={() => handleSubmit()}
                        nextLabel="Save & next"
                      />
                    </div>
                  </div>
                )}
              </div>
              )}
        </div>
      </ClientAppShell>
    {axlPilotChatContext ? (
      <Copilot
        key={`${axlPilotChatContext.capability_id}-${axlPilotChatContext.sub_capability_id ?? "direct"}`}
        chatContext={axlPilotChatContext}
        userId={userId}
        projectId={projectId}
        userName={authUser?.userName}
        formState={subCapabilityState}
        formConfig={formConfig}
        assistantAvailable={assistantAvailable}
      />
    ) : null}
    </>
  );
}

export default Assessment;
